// Small JSON values the station keeps about itself, in the local-only `device_state` table
// (so they survive restarts and live in the same database as the outbox).

import type { LocalReader, LocalTx } from './engine.js';

/** Keys used in `device_state`. */
export const DEVICE_STATE_KEYS = {
  /** `PinLockoutState` from @enp/domain (per device, plan §10.2). */
  pinLockout: 'pin_lockout',
  /**
   * The newest Admin unlock (`device.pin_unlock_requested_at`, B-080) this station has
   * applied: each newer one clears the PIN lock once.
   */
  pinUnlockSeen: 'pin_unlock_seen',
  /** The last entitlement token from `/devices/config` (plan §10.5). */
  entitlement: 'entitlement',
  /** Entitlement public keys by kid (raw Ed25519, base64url), from `/entitlements/keys`. */
  entitlementKeys: 'entitlement_keys',
  /** The minimum-version gate (plan §9.5): set by `upgrade_required`, cleared by an update. */
  upgradeGate: 'upgrade_gate',
  /** The last `/devices/config` answer's station, device and clock reading. */
  deviceConfig: 'device_config',
  /** The stock take in progress on this station (survives a reload or a sign-out). */
  stockTakeDraft: 'stock_take_draft',
  /**
   * Moves and write-offs saved on this station whose effect isn't in the stock figures yet
   * (UI4S-204): the station shows the figures after them and checks amounts against those.
   * Each goes once its command is answered and a download has come in after that.
   */
  pendingStock: 'pending_stock',
  /**
   * Locations whose stock figures stay hidden after a blind count there was submitted (until
   * the server's figures after it have synced) or discarded (for 30 minutes), D-057.
   */
  blindHolds: 'blind_holds',
} as const;

export type DeviceStateKey = (typeof DEVICE_STATE_KEYS)[keyof typeof DEVICE_STATE_KEYS];

/** Reads a JSON value, or null when absent or unreadable. */
export async function readDeviceState<T>(db: LocalReader, key: DeviceStateKey): Promise<T | null> {
  const row = await db.getOptional<{ value: string | null }>(
    'SELECT value FROM device_state WHERE id = ?',
    [key],
  );
  if (!row?.value) return null;
  try {
    return JSON.parse(row.value) as T;
  } catch {
    return null;
  }
}

/** Writes (or, with null, deletes) a JSON value. */
export async function writeDeviceState(
  tx: LocalTx,
  key: DeviceStateKey,
  value: unknown,
  now: Date,
): Promise<void> {
  if (value === null || value === undefined) {
    await tx.execute('DELETE FROM device_state WHERE id = ?', [key]);
    return;
  }
  const json = JSON.stringify(value);
  // PowerSync tables are views: SQLite does not count rows changed through their INSTEAD OF
  // triggers, so look before writing instead of trusting `rowsAffected`.
  const exists = await tx.getOptional<{ id: string }>('SELECT id FROM device_state WHERE id = ?', [
    key,
  ]);
  if (exists) {
    await tx.execute('UPDATE device_state SET value = ?, updated_at = ? WHERE id = ?', [
      json,
      now.toISOString(),
      key,
    ]);
  } else {
    await tx.execute('INSERT INTO device_state (id, value, updated_at) VALUES (?, ?, ?)', [
      key,
      json,
      now.toISOString(),
    ]);
  }
}
